import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Profile } from "@/integrations/supabase/types/subscription";
import { useNavigate } from "react-router-dom";
import { Shield, KeyRound, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SecurityCardProps {
  profile: Profile;
  onUpdate: (updates: Partial<Profile>) => Promise<void>;
}

export function SecurityCard({ profile, onUpdate }: SecurityCardProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [local2FAState, setLocal2FAState] = useState(profile.two_factor_enabled ?? false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handle2FAChange = async (checked: boolean) => {
    if (isUpdating) return;
    
    setLocal2FAState(checked);
    setIsUpdating(true);

    try {
      if (checked) {
        navigate("/2fa-setup");
      } else {
        await onUpdate({ two_factor_enabled: false });
        
        toast({
          title: "2FA deaktivert",
          description: "Tofaktorautentisering er nå deaktivert",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('2FA update error:', error);
      setLocal2FAState(!checked);
      
      toast({
        title: "Kunne ikke oppdatere 2FA-innstillinger",
        description: "Vennligst prøv igjen senere",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card className="transition-all duration-300 hover:shadow-lg bg-white/50 backdrop-blur-sm">
      <CardHeader className="space-y-1">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-orange-100">
            <Shield className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <CardTitle className="text-xl text-orange-900">Sikkerhet</CardTitle>
            <CardDescription className="text-sm text-orange-600/80">
              Administrer sikkerhetsinnstillinger
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div 
          className="flex items-center justify-between gap-4"
          role="group"
          aria-labelledby="2fa-label"
        >
          <div className="flex-1">
            <Label id="2fa-label" htmlFor="2fa" className="text-sm text-orange-900">
              Tofaktorautentisering (2FA)
            </Label>
            <p className="text-xs text-orange-600/80 mt-1">
              Øk sikkerheten ved å kreve både passord og en engangskode ved innlogging
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="2fa"
              checked={local2FAState}
              onCheckedChange={handle2FAChange}
              className="data-[state=checked]:bg-orange-600"
              disabled={isUpdating}
              aria-label="Aktiver tofaktorautentisering"
            />
            {isUpdating && (
              <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/change-password")}
            className="w-full sm:w-auto border-orange-200 hover:bg-orange-50 text-orange-700 focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            aria-label="Endre passord"
          >
            <KeyRound className="h-4 w-4 mr-2 text-orange-600" />
            Endre passord
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}